import React from "react";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import QuestionGroups from "./QuestionGroups";
import ServiceNav from "./ServiceNav";
import FixedAnswerNotes from "./FixedAnswerNotes";

// Stage 3. One service at a time, its questions in the sections the pricing
// platform already groups them into.
export default function ServiceStage({
  sections,
  index,
  onGo,
  answers,
  onChange,
  missingKeys,
  onBack,
  onNext,
  nextLabel,
  requiresBookkeepingIds = [],
  bookkeepingReady = false,
  bookkeepingName = "Monthly Bookkeeping",
}) {
  const sec = sections[index];
  if (!sec) return null;
  const empty = sec.groups.length === 0;
  const needsBookkeepingFirst = requiresBookkeepingIds.includes(sec.service.service_id) && !bookkeepingReady;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-6 lg:gap-8 items-start" data-testid="pricing-service-stage">
      <div className="min-w-0 lg:sticky lg:top-28">
        <ServiceNav sections={sections} index={index} onGo={onGo} />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-400">{index + 1} of {sections.length}</p>
        <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl mt-1" data-testid="pricing-service-heading">
          {sec.service.name}
        </h2>
        {(sec.service.display?.note || sec.service.description) && (
          <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl leading-relaxed">
            {sec.service.display?.note || sec.service.description}
          </p>
        )}

        <div className="mt-6 space-y-4">
          <FixedAnswerNotes notes={sec.notes} testId={`pricing-service-notes-${sec.service.service_id}`} />
          {empty ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pricing-service-no-questions">
              <p className="text-sm text-slate-600 flex items-start gap-2 leading-relaxed">
                <Info size={15} className="text-orange shrink-0 mt-0.5" />
                {needsBookkeepingFirst
                  ? `We can't price this on its own — finish ${bookkeepingName} and this is included alongside it.`
                  : "Nothing to ask about this one. It's included in your package exactly as selected."}
              </p>
            </div>
          ) : (
            <QuestionGroups
              groups={sec.groups}
              answers={answers}
              onChange={onChange}
              missingKeys={missingKeys}
              testPrefix={`pricing-service-${sec.service.service_id}`}
            />
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            data-testid="pricing-service-back"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-navy font-semibold text-sm transition-colors"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            type="button"
            onClick={onNext}
            data-testid="pricing-service-next"
            className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
          >
            {nextLabel} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
